import { CharacterType, ICharacter, useGroupchat, useUserInfo } from "@next-platform/client-common-core";
import { useCallback, useEffect, useRef, useState } from "react";

import { FetchGroupchatsParams, fetchGroupchatsRequest, fetchStandardChatbotsRequest, GroupchatServerParams } from "./useMyChatbotsHelpers";

interface IUseMyChatbotsProps {
  type?: CharacterType;
  includeAttachedLorebookId?: boolean;
  // When false, no fetch is performed and the hook stays idle. Used to skip a
  // second character type (e.g. groups) when a feature flag is off.
  isFetchEnabled?: boolean;
}

interface IUseMyChatbotsReturn {
  chatbots: ICharacter[] | null;
  isLoading: boolean;
  isLoadingMoreGroupchats: boolean;
  chatbotsLastKey: string | null;
  error: Error | null;
  refetch: () => Promise<void>;
  loadMoreGroupchats: (params?: GroupchatServerParams) => void;
  refetchGroupchatsWith: (params?: GroupchatServerParams) => Promise<void>;
}

export const useMyChatbots = ({
  type = CharacterType.STANDARD,
  includeAttachedLorebookId,
  isFetchEnabled = true,
}: IUseMyChatbotsProps = {}): IUseMyChatbotsReturn => {
  const [stateChatbots, setStateChatbots] = useState<ICharacter[] | null>(null);
  const [stateIsLoading, setStateIsLoading] = useState(false);
  const [stateIsLoadingMoreGroupchats, setStateIsLoadingMoreGroupchats] = useState(false);
  const [stateChatbotsLastKey, setStateChatbotsLastKey] = useState<string | null>(null);
  const [stateError, setStateError] = useState<Error | null>(null);
  const { onlyUserId: userId } = useUserInfo();
  const { getGroupchatsData } = useGroupchat();
  const isGroupchatType = type === CharacterType.META;
  const refGetGroupchatsData = useRef(getGroupchatsData);
  const refFetchId = useRef(0);

  useEffect(() => {
    refGetGroupchatsData.current = getGroupchatsData;
  }, [getGroupchatsData]);

  const fetchGroupchats = useCallback(
    async (params: FetchGroupchatsParams = {}) => {
      const fetchId = ++refFetchId.current;
      await fetchGroupchatsRequest({
        fetchId,
        getGroupchatsData: refGetGroupchatsData.current,
        includeAttachedLorebookId,
        params,
        refFetchId,
        setStateChatbots,
        setStateChatbotsLastKey,
        setStateError,
        setStateIsLoading,
        setStateIsLoadingMoreGroupchats,
      });
    },
    [includeAttachedLorebookId],
  );

  const fetchStandardChatbots = useCallback(
    async (params: FetchGroupchatsParams = {}) => {
      const fetchId = ++refFetchId.current;
      await fetchStandardChatbotsRequest({
        fetchId,
        includeAttachedLorebookId,
        params,
        refFetchId,
        setStateChatbots,
        setStateChatbotsLastKey,
        setStateError,
        setStateIsLoading,
        setStateIsLoadingMoreGroupchats,
        type,
      });
    },
    [type, includeAttachedLorebookId],
  );

  const fetchChatbots = useCallback(async () => {
    if (!isFetchEnabled) return;

    if (!userId) {
      if (!isGroupchatType) setStateChatbots([]);
      setStateError(null);
      return;
    }

    setStateChatbotsLastKey(null);
    if (isGroupchatType) {
      await fetchGroupchats({});
      return;
    }
    await fetchStandardChatbots({});
  }, [isFetchEnabled, userId, isGroupchatType, fetchGroupchats, fetchStandardChatbots]);

  useEffect(() => {
    void fetchChatbots();
  }, [fetchChatbots]);

  const loadMoreGroupchats = useCallback(
    (params?: GroupchatServerParams) => {
      if (!stateChatbotsLastKey) return;
      const fetchFn = isGroupchatType ? fetchGroupchats : fetchStandardChatbots;
      void fetchFn({ lastKey: stateChatbotsLastKey, isLoadMore: true, ...params });
    },
    [stateChatbotsLastKey, isGroupchatType, fetchGroupchats, fetchStandardChatbots],
  );

  const refetchGroupchatsWith = useCallback(
    async (params?: GroupchatServerParams) => {
      if (!isFetchEnabled) return;
      setStateChatbots(null);
      setStateChatbotsLastKey(null);
      const fetchFn = isGroupchatType ? fetchGroupchats : fetchStandardChatbots;
      await fetchFn({ ...params });
    },
    [isFetchEnabled, isGroupchatType, fetchGroupchats, fetchStandardChatbots],
  );

  return {
    chatbots: stateChatbots,
    isLoading: stateIsLoading,
    isLoadingMoreGroupchats: stateIsLoadingMoreGroupchats,
    chatbotsLastKey: stateChatbotsLastKey,
    error: stateError,
    refetch: fetchChatbots,
    loadMoreGroupchats,
    refetchGroupchatsWith,
  };
};

export default useMyChatbots;
